/**
 * Platform Translation Service
 * Handles translation of all UI text using AI
 */

import { apiService } from './api';

export interface TranslationKeys {
  // Navigation
  'nav.home': string;
  'nav.products': string;
  'nav.tutorials': string;
  'nav.admin': string;
  'nav.settings': string;
  
  // Common actions
  'common.create': string;
  'common.edit': string;
  'common.delete': string;
  'common.save': string;
  'common.cancel': string;
  'common.back': string;
  'common.next': string;
  'common.previous': string;
  'common.search': string;
  'common.filter': string;
  'common.loading': string;
  'common.error': string;
  'common.success': string;
  
  // Tutorial related
  'tutorial.title': string;
  'tutorial.description': string;
  'tutorial.difficulty': string;
  'tutorial.estimatedTime': string;
  'tutorial.category': string;
  'tutorial.steps': string;
  'tutorial.start': string;
  'tutorial.continue': string;
  'tutorial.complete': string;
  'tutorial.progress': string;
  'tutorial.timeRemaining': string;
  'tutorial.markComplete': string;
  'tutorial.createNew': string;
  'tutorial.editTutorial': string;
  'tutorial.deleteTutorial': string;
  
  // Difficulty levels
  'difficulty.beginner': string;
  'difficulty.intermediate': string;
  'difficulty.advanced': string;
  
  // Categories
  'category.gettingStarted': string;
  'category.productGuides': string;
  'category.advanced': string;
  'category.troubleshooting': string;
  
  // Products
  'product.title': string;
  'product.description': string;
  'product.category': string;
  'product.createNew': string;
  'product.editProduct': string;
  'product.deleteProduct': string;
  
  // Admin
  'admin.title': string;
  'admin.tutorials': string;
  'admin.products': string;
  'admin.users': string;
  'admin.settings': string;
  'admin.import': string;
  'admin.export': string;
  'admin.bulkDelete': string;
  
  // Settings
  'settings.title': string;
  'settings.language': string;
  'settings.theme': string;
  'settings.notifications': string;
  'settings.account': string;
  
  // Language
  'language.english': string;
  'language.spanish': string;
  'language.switchTo': string;
  'language.translateTo': string;
  'language.translating': string;
  'language.translationComplete': string;
  'language.translationFailed': string;
  
  // Messages
  'message.noResults': string;
  'message.noTutorials': string;
  'message.noProducts': string;
  'message.loadingTutorials': string;
  'message.loadingProducts': string;
  'message.tutorialSaved': string;
  'message.tutorialDeleted': string;
  'message.productSaved': string;
  'message.productDeleted': string;
  'message.translationStarted': string;
  'message.translationCompleted': string;
  'message.translationFailed': string;
  
  // Form labels
  'form.title': string;
  'form.description': string;
  'form.difficulty': string;
  'form.estimatedTime': string;
  'form.category': string;
  'form.product': string;
  'form.stepTitle': string;
  'form.stepContent': string;
  'form.stepType': string;
  'form.required': string;
  'form.optional': string;
  
  // Step types
  'stepType.text': string;
  'stepType.video': string;
  'stepType.code': string;
  'stepType.quiz': string;
  'stepType.pdf': string;
  
  // Time units
  'time.minutes': string;
  'time.hours': string;
  'time.days': string;
  
  // Status
  'status.published': string;
  'status.draft': string;
  'status.archived': string;
  'status.active': string;
  'status.inactive': string;
}

class PlatformTranslationService {
  private translations: Map<string, TranslationKeys> = new Map();
  private isTranslating = false;

  constructor() {
    // Initialize with English translations
    this.initializeEnglishTranslations();
  }

  /**
   * Get translation for a key in the current language
   */
  getTranslation(key: keyof TranslationKeys, language: string = 'en'): string {
    const langTranslations = this.translations.get(language);
    if (langTranslations && langTranslations[key]) {
      return langTranslations[key];
    }
    
    // Fallback to English
    const englishTranslations = this.translations.get('en');
    return englishTranslations?.[key] || key;
  }

  /**
   * Get all translations for a language
   */
  getTranslations(language: string): TranslationKeys | null {
    return this.translations.get(language) || null;
  }

  /**
   * Check if a language is available
   */
  isLanguageAvailable(language: string): boolean {
    return this.translations.has(language);
  }

  /**
   * Translate platform to a new language using AI
   */
  async translatePlatform(targetLanguage: string): Promise<boolean> {
    if (this.isTranslating) {
      return false;
    }

    try {
      this.isTranslating = true;

      // Get English translations as source
      const englishTranslations = this.translations.get('en');
      if (!englishTranslations) {
        throw new Error('English translations not found');
      }

      // Use AI to translate all keys
      const translatedKeys = await this.translateWithAI(englishTranslations, targetLanguage);
      
      // Store translated translations
      this.translations.set(targetLanguage, translatedKeys);
      
      // Save to localStorage for persistence
      localStorage.setItem(`platform-translations-${targetLanguage}`, JSON.stringify(translatedKeys));
      
      return true;
    } catch (error) {
      console.error('Platform translation failed:', error);
      return false;
    } finally {
      this.isTranslating = false;
    }
  }

  /**
   * Load translations from localStorage
   */
  loadTranslationsFromStorage(language: string): boolean {
    try {
      const stored = localStorage.getItem(`platform-translations-${language}`);
      if (stored) {
        const translations = JSON.parse(stored);
        this.translations.set(language, translations);
        return true;
      }
    } catch (error) {
      console.error('Failed to load translations from storage:', error);
    }
    return false;
  }

  /**
   * Translate using AI service
   */
  private async translateWithAI(englishTranslations: TranslationKeys, targetLanguage: string): Promise<TranslationKeys> {
    try {
      
      const response = await apiService.request('/translation/translate-platform', {
        method: 'POST',
        body: JSON.stringify({
          translations: englishTranslations,
          targetLanguage
        })
      });

      return response.translations;
    } catch (error) {
      console.error('AI translation failed:', error);
      // Fallback to manual translation for common keys
      return this.getFallbackTranslations(targetLanguage);
    }
  }

  /**
   * Get fallback translations for common keys
   */
  private getFallbackTranslations(targetLanguage: string): TranslationKeys {
    if (targetLanguage === 'es') {
      return {
        // Navigation
        'nav.home': 'Inicio',
        'nav.products': 'Productos',
        'nav.tutorials': 'Tutoriales',
        'nav.admin': 'Administración',
        'nav.settings': 'Configuración',
        
        // Common actions
        'common.create': 'Crear',
        'common.edit': 'Editar',
        'common.delete': 'Eliminar',
        'common.save': 'Guardar',
        'common.cancel': 'Cancelar',
        'common.back': 'Atrás',
        'common.next': 'Siguiente',
        'common.previous': 'Anterior',
        'common.search': 'Buscar',
        'common.filter': 'Filtrar',
        'common.loading': 'Cargando...',
        'common.error': 'Error',
        'common.success': 'Éxito',
        
        // Tutorial related
        'tutorial.title': 'Título',
        'tutorial.description': 'Descripción',
        'tutorial.difficulty': 'Dificultad',
        'tutorial.estimatedTime': 'Tiempo Estimado',
        'tutorial.category': 'Categoría',
        'tutorial.steps': 'Pasos',
        'tutorial.start': 'Comenzar',
        'tutorial.continue': 'Continuar',
        'tutorial.complete': 'Completar',
        'tutorial.progress': 'Progreso',
        'tutorial.timeRemaining': 'Tiempo Restante',
        'tutorial.markComplete': 'Marcar como Completado',
        'tutorial.createNew': 'Crear Nuevo Tutorial',
        'tutorial.editTutorial': 'Editar Tutorial',
        'tutorial.deleteTutorial': 'Eliminar Tutorial',
        
        // Difficulty levels
        'difficulty.beginner': 'Principiante',
        'difficulty.intermediate': 'Intermedio',
        'difficulty.advanced': 'Avanzado',
        
        // Categories
        'category.gettingStarted': 'Primeros Pasos',
        'category.productGuides': 'Guías de Producto',
        'category.advanced': 'Avanzado',
        'category.troubleshooting': 'Solución de Problemas',
        
        // Products
        'product.title': 'Título',
        'product.description': 'Descripción',
        'product.category': 'Categoría',
        'product.createNew': 'Crear Nuevo Producto',
        'product.editProduct': 'Editar Producto',
        'product.deleteProduct': 'Eliminar Producto',
        
        // Admin
        'admin.title': 'Administración',
        'admin.tutorials': 'Tutoriales',
        'admin.products': 'Productos',
        'admin.users': 'Usuarios',
        'admin.settings': 'Configuración',
        'admin.import': 'Importar',
        'admin.export': 'Exportar',
        'admin.bulkDelete': 'Eliminación Masiva',
        
        // Settings
        'settings.title': 'Configuración',
        'settings.language': 'Idioma',
        'settings.theme': 'Tema',
        'settings.notifications': 'Notificaciones',
        'settings.account': 'Cuenta',
        
        // Language
        'language.english': 'Inglés',
        'language.spanish': 'Español',
        'language.switchTo': 'Cambiar a',
        'language.translateTo': 'Traducir a',
        'language.translating': 'Traduciendo...',
        'language.translationComplete': 'Traducción Completada',
        'language.translationFailed': 'Traducción Fallida',
        
        // Messages
        'message.noResults': 'No se encontraron resultados',
        'message.noTutorials': 'No hay tutoriales disponibles',
        'message.noProducts': 'No hay productos disponibles',
        'message.loadingTutorials': 'Cargando tutoriales...',
        'message.loadingProducts': 'Cargando productos...',
        'message.tutorialSaved': 'Tutorial guardado exitosamente',
        'message.tutorialDeleted': 'Tutorial eliminado exitosamente',
        'message.productSaved': 'Producto guardado exitosamente',
        'message.productDeleted': 'Producto eliminado exitosamente',
        'message.translationStarted': 'Traducción iniciada',
        'message.translationCompleted': 'Traducción completada',
        'message.translationFailed': 'Traducción fallida',
        
        // Form labels
        'form.title': 'Título',
        'form.description': 'Descripción',
        'form.difficulty': 'Dificultad',
        'form.estimatedTime': 'Tiempo Estimado',
        'form.category': 'Categoría',
        'form.product': 'Producto',
        'form.stepTitle': 'Título del Paso',
        'form.stepContent': 'Contenido del Paso',
        'form.stepType': 'Tipo de Paso',
        'form.required': 'Requerido',
        'form.optional': 'Opcional',
        
        // Step types
        'stepType.text': 'Texto',
        'stepType.video': 'Video',
        'stepType.code': 'Código',
        'stepType.quiz': 'Cuestionario',
        'stepType.pdf': 'PDF',
        
        // Time units
        'time.minutes': 'minutos',
        'time.hours': 'horas',
        'time.days': 'días',
        
        // Status
        'status.published': 'Publicado',
        'status.draft': 'Borrador',
        'status.archived': 'Archivado',
        'status.active': 'Activo',
        'status.inactive': 'Inactivo'
      };
    }
    
    // Return English as fallback
    return this.translations.get('en')!;
  }

  /**
   * Initialize English translations
   */
  private initializeEnglishTranslations(): void {
    const englishTranslations: TranslationKeys = {
      // Navigation
      'nav.home': 'Home',
      'nav.products': 'Products',
      'nav.tutorials': 'Tutorials',
      'nav.admin': 'Admin',
      'nav.settings': 'Settings',
      
      // Common actions
      'common.create': 'Create',
      'common.edit': 'Edit',
      'common.delete': 'Delete',
      'common.save': 'Save',
      'common.cancel': 'Cancel',
      'common.back': 'Back',
      'common.next': 'Next',
      'common.previous': 'Previous',
      'common.search': 'Search',
      'common.filter': 'Filter',
      'common.loading': 'Loading...',
      'common.error': 'Error',
      'common.success': 'Success',
      
      // Tutorial related
      'tutorial.title': 'Title',
      'tutorial.description': 'Description',
      'tutorial.difficulty': 'Difficulty',
      'tutorial.estimatedTime': 'Estimated Time',
      'tutorial.category': 'Category',
      'tutorial.steps': 'Steps',
      'tutorial.start': 'Start',
      'tutorial.continue': 'Continue',
      'tutorial.complete': 'Complete',
      'tutorial.progress': 'Progress',
      'tutorial.timeRemaining': 'Time Remaining',
      'tutorial.markComplete': 'Mark as Complete',
      'tutorial.createNew': 'Create New Tutorial',
      'tutorial.editTutorial': 'Edit Tutorial',
      'tutorial.deleteTutorial': 'Delete Tutorial',
      
      // Difficulty levels
      'difficulty.beginner': 'Beginner',
      'difficulty.intermediate': 'Intermediate',
      'difficulty.advanced': 'Advanced',
      
      // Categories
      'category.gettingStarted': 'Getting Started',
      'category.productGuides': 'Product Guides',
      'category.advanced': 'Advanced',
      'category.troubleshooting': 'Troubleshooting',
      
      // Products
      'product.title': 'Title',
      'product.description': 'Description',
      'product.category': 'Category',
      'product.createNew': 'Create New Product',
      'product.editProduct': 'Edit Product',
      'product.deleteProduct': 'Delete Product',
      
      // Admin
      'admin.title': 'Administration',
      'admin.tutorials': 'Tutorials',
      'admin.products': 'Products',
      'admin.users': 'Users',
      'admin.settings': 'Settings',
      'admin.import': 'Import',
      'admin.export': 'Export',
      'admin.bulkDelete': 'Bulk Delete',
      
      // Settings
      'settings.title': 'Settings',
      'settings.language': 'Language',
      'settings.theme': 'Theme',
      'settings.notifications': 'Notifications',
      'settings.account': 'Account',
      
      // Language
      'language.english': 'English',
      'language.spanish': 'Spanish',
      'language.switchTo': 'Switch to',
      'language.translateTo': 'Translate to',
      'language.translating': 'Translating...',
      'language.translationComplete': 'Translation Complete',
      'language.translationFailed': 'Translation Failed',
      
      // Messages
      'message.noResults': 'No results found',
      'message.noTutorials': 'No tutorials available',
      'message.noProducts': 'No products available',
      'message.loadingTutorials': 'Loading tutorials...',
      'message.loadingProducts': 'Loading products...',
      'message.tutorialSaved': 'Tutorial saved successfully',
      'message.tutorialDeleted': 'Tutorial deleted successfully',
      'message.productSaved': 'Product saved successfully',
      'message.productDeleted': 'Product deleted successfully',
      'message.translationStarted': 'Translation started',
      'message.translationCompleted': 'Translation completed',
      'message.translationFailed': 'Translation failed',
      
      // Form labels
      'form.title': 'Title',
      'form.description': 'Description',
      'form.difficulty': 'Difficulty',
      'form.estimatedTime': 'Estimated Time',
      'form.category': 'Category',
      'form.product': 'Product',
      'form.stepTitle': 'Step Title',
      'form.stepContent': 'Step Content',
      'form.stepType': 'Step Type',
      'form.required': 'Required',
      'form.optional': 'Optional',
      
      // Step types
      'stepType.text': 'Text',
      'stepType.video': 'Video',
      'stepType.code': 'Code',
      'stepType.quiz': 'Quiz',
      'stepType.pdf': 'PDF',
      
      // Time units
      'time.minutes': 'minutes',
      'time.hours': 'hours',
      'time.days': 'days',
      
      // Status
      'status.published': 'Published',
      'status.draft': 'Draft',
      'status.archived': 'Archived',
      'status.active': 'Active',
      'status.inactive': 'Inactive'
    };

    this.translations.set('en', englishTranslations);
  }
}

export const platformTranslationService = new PlatformTranslationService();
export default platformTranslationService;
